import {deserialize, parseQuery, serializableTypes, serialize} from './encoder';

export interface RequestOptions {
    credentials?: boolean;
    onError?: (err: RequestError, request: () => Promise<Response>) => Promise<Response | void>;
    use?: Function[];
}

type ContentType = 'text' | 'json' | 'form' | 'multipart';
type RequestBody = object | string | FormData;

export class Request {
    private readonly apiRoot: string;
    private readonly credentials: 'same-origin' | 'include';
    private readonly onError: RequestOptions['onError'];
    private readonly middlewares: RequestOptions['use'];

    constructor(apiRoot: string, options: RequestOptions = {}) {
        this.apiRoot = apiRoot;
        this.onError = options.onError;
        this.middlewares = options.use;
        this.credentials = options.credentials ? 'include' : 'same-origin';
    }

    get = async (url: string, query?: object | string, body?: object) => {
        await this.handleMiddlewares();

        let headers = this.setHeaders('GET');
        if(body) {
            headers = {
                ...headers,
                'Content-Type': serializableTypes.json,
            };
        }

        const request = () => fetch(this.apiRoot + url + parseQuery(query), {
            method: 'GET',
            credentials: this.credentials,
            body: body ? serialize(body, 'json') : undefined,
            headers,
        });
        return this.handleRequest(request);
    };

    del = async (url: string, body?: object) => {
        await this.handleMiddlewares();

        let headers = this.setHeaders('DELETE');
        if(body) {
            headers = {
                ...headers,
                'Content-Type': serializableTypes.json,
            };
        }

        const request = () => fetch(this.apiRoot + url, {
            method: 'DELETE',
            credentials: this.credentials,
            body: body ? serialize(body, 'json') : undefined,
            headers,
        });
        return this.handleRequest(request);
    };

    put = async (url: string, body: RequestBody, contentType: ContentType = 'json') => {
        await this.handleMiddlewares();
        const request = () => fetch(this.apiRoot + url, {
            method: 'PUT',
            credentials: this.credentials,
            body: serialize(body, contentType),
            headers: {
                ...this.setHeaders('PUT'),
                'Content-Type': serializableTypes[contentType],
            },
        });
        return this.handleRequest(request);
    };

    patch = async (url: string, body: RequestBody, contentType: ContentType = 'json') => {
        await this.handleMiddlewares();
        const request = () => fetch(this.apiRoot + url, {
            method: 'PATCH',
            credentials: this.credentials,
            body: serialize(body, contentType),
            headers: {
                ...this.setHeaders('PATCH'),
                'Content-Type': serializableTypes[contentType],
            },
        });
        return this.handleRequest(request);
    };

    post = async (url: string, body: RequestBody, contentType: ContentType = 'json') => {
        await this.handleMiddlewares();
        const request = () => fetch(this.apiRoot + url, {
            method: 'POST',
            credentials: this.credentials,
            body: serialize(body, contentType),
            headers: {
                ...this.setHeaders('POST'),
                'Content-Type': serializableTypes[contentType],
            },
        });
        return this.handleRequest(request);
    };

    setHeaders: (method: string) => RequestInit['headers'] | undefined = () => undefined;

    private handleRequest = async (request: () => Promise<Response>) => {
        let res = await request();
        if (res.status >= 300) {
            res = await this.handleError(res, request);
        }
        return deserialize(res);
    };

    private handleError = async (res: Response, request: () => Promise<Response>) => {
        const message = await res.text();
        const error = new RequestError(message || res.statusText, res.status);
        if (!this.onError) throw error;
        const newResponse = await this.onError(error, request);
        if (!newResponse) throw error;
        return newResponse;
    };

    private handleMiddlewares = async () => {
        if (!this.middlewares) return;
        for (const func of this.middlewares) {
            await func();
        }
    };
}

export class RequestError extends Error {
    status: number;

    constructor(message: string, status: number) {
        super(message);
        this.name = 'RequestError';
        this.message = message;
        this.status = status;
    }
}
